import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import { ceil, map } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import Chevron from 'components/Chevron';
import { mediumUp } from 'constants/mediaQueries';
import { breakpoints, cn } from 'utils/Tailwind';

import UgcCarouselCard from './UgcCarouselCard';

import '@splidejs/react-splide/css';
import 'styles/tailwind.css';

export const UGC_CAROUSEL_VARIATIONS = {
  SINGLE_CARD_CAROUSEL: 'singleCardCarouselVariation',
  DOUBLE_CARD_CAROUSEL: 'doubleCardCarouselVariation',
  STACKED_GRID: 'stackedVariation',
};

export const DESKTOP_CONFIGURATION = {
  CAROUSEL: 'Carousel',
  STACKED_GRID: 'Stacked Grid',
};

export const MOBILE_CONFIGURATION = {
  SINGLE_CARD_CAROUSEL: 'Single Card Carousel',
  DOUBLE_CARD_CAROUSEL: 'Double Card Carousel',
};

const SLIDES_PER_PAGE = {
  DEFAULT: 1,
  LG: 4,
};

const CAROUSEL_OPTIONS = {
  type: 'slide',
  pagination: false,
  gap: '0.5rem',
  mediaQuery: 'min',
  breakpoints: {
    0: {
      perPage: SLIDES_PER_PAGE.DEFAULT,
      padding: {
        left: '0',
        right: '28vw',
      },
    },
    [breakpoints.lg]: {
      perPage: SLIDES_PER_PAGE.LG,
      padding: {
        left: '0',
        right: '0',
      },
    },
    [breakpoints['2xl']]: {
      perPage: SLIDES_PER_PAGE.LG,
      padding: {
        left: '0',
        right: '0',
      },
    },
  },
};

const usePageSectionCardCarousel = (
  desktopConfiguration,
  imageAndSubtextList,
  isMediumUp,
  mobileConfiguration
) => {
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const carouselRef = useRef(null);

  const stackedVariation =
    isMediumUp && desktopConfiguration === DESKTOP_CONFIGURATION.STACKED_GRID;

  const singleCardCarouselVariation =
    (isMediumUp && desktopConfiguration === DESKTOP_CONFIGURATION.CAROUSEL) ||
    (!isMediumUp && mobileConfiguration === MOBILE_CONFIGURATION.SINGLE_CARD_CAROUSEL);

  const doubleCardCarouselVariation =
    !isMediumUp && mobileConfiguration === MOBILE_CONFIGURATION.DOUBLE_CARD_CAROUSEL;

  let variation = '';

  if (stackedVariation) {
    variation = UGC_CAROUSEL_VARIATIONS.STACKED_GRID;
  } else if (doubleCardCarouselVariation) {
    variation = UGC_CAROUSEL_VARIATIONS.DOUBLE_CARD_CAROUSEL;
  } else if (singleCardCarouselVariation) {
    variation = UGC_CAROUSEL_VARIATIONS.SINGLE_CARD_CAROUSEL;
  }

  let doubleCardCarouselList = [];

  if (doubleCardCarouselVariation) {
    const halfIndex = ceil(imageAndSubtextList.length / 2);
    doubleCardCarouselList = imageAndSubtextList.slice(0, halfIndex).map((item, index) => {
      const bottomItem = imageAndSubtextList[index + halfIndex];
      return {
        top: { src: item.src, subtext: item.subtext },
        ...(bottomItem && { bottom: { src: bottomItem.src, subtext: bottomItem.subtext } }),
      };
    });
  }

  const handleOnMove = useCallback(({ index }) => {
    setActiveSlideIndex(index);
  }, []);

  return {
    activeSlideIndex,
    carouselRef,
    doubleCardCarouselList,
    handleOnMove,
    items: doubleCardCarouselList.length ? doubleCardCarouselList : imageAndSubtextList,
    variation,
  };
};

const PdpUgcCarousel = ({ className, data, pdpStorytellingEnabled, dataTestId }) => {
  const { desktopConfiguration, mobileConfiguration, heading, imageAndSubtextList } = data;

  const isMediumUp = useMediaQuery({ query: mediumUp });

  const { activeSlideIndex, carouselRef, handleOnMove, items, variation } =
    usePageSectionCardCarousel(
      desktopConfiguration,
      imageAndSubtextList,
      isMediumUp,
      mobileConfiguration
    );

  return (
    <section
      className={cn('PdpUgcCarousel overflow-hidden', className)}
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      <div
        className={cn('mx-auto w-full max-w-[1380px] px-6', {
          'max-w-full md:px-10': pdpStorytellingEnabled,
        })}
      >
        <div className={cn(`${variation} relative overflow-visible`)}>
          <p className="mb-2 font-bold uppercase tracking-wide md:mb-8">{heading}</p>

          {variation === UGC_CAROUSEL_VARIATIONS.STACKED_GRID && (
            <div className="md:max-2xl grid grid-cols-2 gap-2 md:grid-cols-4">
              {map(items, ({ src, subtext }) => (
                <UgcCarouselCard
                  key={src}
                  src={src}
                  subtext={subtext}
                  className="relative aspect-6/7"
                />
              ))}
            </div>
          )}
          {variation === UGC_CAROUSEL_VARIATIONS.SINGLE_CARD_CAROUSEL && (
            <Splide
              data-testid="splide"
              hasTrack={false}
              options={CAROUSEL_OPTIONS}
              onMove={handleOnMove}
              ref={carouselRef}
            >
              <CarouselArrows activeSlideIndex={activeSlideIndex} items={items} />
              <SplideTrack className="overflow-visible">
                {map(items, ({ src, subtext }) => (
                  <SplideSlide
                    key={src}
                    className="relative flex aspect-6/7 h-full w-full flex-col"
                  >
                    <UgcCarouselCard key={src} src={src} subtext={subtext} />
                  </SplideSlide>
                ))}
              </SplideTrack>
            </Splide>
          )}
          {variation === UGC_CAROUSEL_VARIATIONS.DOUBLE_CARD_CAROUSEL && (
            <Splide
              data-testid="splide"
              hasTrack={false}
              options={CAROUSEL_OPTIONS}
              onMove={handleOnMove}
              ref={carouselRef}
            >
              <CarouselArrows activeSlideIndex={activeSlideIndex} items={items} />
              <SplideTrack className="overflow-visible">
                {map(
                  items,
                  ({ top = { src: '', subText: '' }, bottom = { src: '', subText: '' } }) => (
                    <SplideSlide
                      key={`${top.src}-${bottom.src}`}
                      className="relative flex aspect-6/7 h-full w-full flex-col"
                    >
                      <UgcCarouselCard {...top} className="mb-2" />
                      {bottom && <UgcCarouselCard {...bottom} />}
                    </SplideSlide>
                  )
                )}
              </SplideTrack>
            </Splide>
          )}
        </div>
      </div>
    </section>
  );
};

const CarouselArrows = ({ activeSlideIndex, items = [] }) => {
  return (
    <div className="splide__arrows hidden md:block">
      <button
        data-testid="previous-arrow"
        className={cn(`splide__arrow splide__arrow--prev !left-6 !bg-white 2xl:!-left-6`, {
          '!hidden': !activeSlideIndex,
        })}
      >
        <Chevron direction="left" size={9} />
      </button>
      <button
        data-testid="next-arrow"
        className={cn(`splide__arrow splide__arrow--next !right-6 !bg-white 2xl:!-right-6`, {
          '!hidden': activeSlideIndex >= items.length - SLIDES_PER_PAGE.LG,
        })}
      >
        <Chevron direction="right" size={9} />
      </button>
    </div>
  );
};

CarouselArrows.propTypes = {
  activeSlideIndex: PropTypes.number,
  items: PropTypes.array,
};

CarouselArrows.defaultProps = {
  activeSlideIndex: 0,
};

PdpUgcCarousel.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    pdpStorytellingEnabled: PropTypes.bool,
    desktopConfiguration: PropTypes.string.isRequired,
    mobileConfiguration: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    imageAndSubtextList: PropTypes.array.isRequired,
  }).isRequired,
  pdpStorytellingEnabled: PropTypes.bool,
  dataTestId: PropTypes.string,
};

export default PdpUgcCarousel;
